import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  formatCurrency,
  formatPts,
  type MemberTxnStatus,
  type MemberTxnType,
} from "./data/mock-member-data";
import { MemberPortalApi } from "@/api/perkpoint/memberPortal";
import { memberErrorMessage } from "@/api/perkpoint/memberPortal";
import { TransactionTable } from "./components/TransactionTable";
import type { MemberTransaction } from "./data/mock-member-data";

export default function MemberTransactions() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState<MemberTxnType | "all">("all");
  const [status, setStatus] = useState<MemberTxnStatus | "all">("all");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<MemberTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, pointsNet: 0, spendTotal: 0, currency: "AED" });
  const [error, setError] = useState("");
  const pageSize = 8;

  useEffect(() => {
    let active = true;
    void MemberPortalApi.transactions({ search, type, status, page, pageSize })
      .then((data) => {
        if (!active) return;
        setRows(
          data.items.map((item) => {
            const at = new Date(item.date);
            return {
              id: item.id,
              date: at.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
              time: at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
              outlet: item.outlet,
              type: item.type,
              amount: item.amount,
              currency: item.currency,
              points: item.points,
              status: item.status,
            };
          }),
        );
        setTotal(data.total);
        setSummary({
          count: data.summary.count,
          pointsNet: data.summary.points_net,
          spendTotal: data.summary.spend_total,
          currency: data.items[0]?.currency || "AED",
        });
        setError("");
      })
      .catch((err: unknown) => {
        if (active) setError(memberErrorMessage(err, "Could not load activity."));
      });
    return () => {
      active = false;
    };
  }, [search, type, status, page]);

  const data = { rows, total, page, pageSize, summary };

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  const clearFilters = () => {
    setSearch("");
    setType("all");
    setStatus("all");
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          Transaction history
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Review earn, redeem, bonus, and adjustment activity
        </p>
        {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase text-slate-500">
            Matching transactions
          </p>
          <p className="mt-2 text-2xl font-bold">{data.summary.count}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase text-slate-500">
            Net points
          </p>
          <p className="mt-2 text-2xl font-bold">
            {formatPts(data.summary.pointsNet)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase text-slate-500">
            Spend total
          </p>
          <p className="mt-2 text-2xl font-bold">
            {formatCurrency(data.summary.spendTotal, summary.currency)}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-end">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search by transaction ID or merchant"
            className="h-10 bg-white pl-9"
          />
        </div>
        <Select
          value={type}
          onValueChange={(v) => {
            setPage(1);
            setType(v as MemberTxnType | "all");
          }}
        >
          <SelectTrigger className="h-10 w-full bg-white lg:w-44">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="Earned">Earned</SelectItem>
            <SelectItem value="Redeemed">Redeemed</SelectItem>
            <SelectItem value="Bonus">Bonus</SelectItem>
            <SelectItem value="Adjustment">Adjustment</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => {
            setPage(1);
            setStatus(v as MemberTxnStatus | "all");
          }}
        >
          <SelectTrigger className="h-10 w-full bg-white lg:w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          className="bg-white"
          onClick={clearFilters}
        >
          Clear filters
        </Button>
      </div>

      <TransactionTable rows={data.rows} />
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Page {page} of {totalPages}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
